import React, { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useEarnAssetDetailsQuery } from 'hooks/queries/Earn/useEarnAssetDetailsQuery'
import { formatAmount, formatAPY } from 'pages/Rewards/utils'
import { earnDetailsBreadcrumbAtom } from './atoms/breadcrumbAtom'
import { EarnAssetDetailsHeader } from './components/EarnAssetDetailsHeader'
import { EarnAssetEarningsSummary } from './components/EarnAssetEarningsSummary'
import { EarnAssetFundsSummary } from './components/EarnAssetFundsSummary'
import { EarnAssetHistory } from './components/EarnAssetHistory'
import { EarnAssetSummaryDirect } from './components/EarnAssetSummaryDirect'
import { useIsDirectEarning } from './hooks/useIsDirectEarning'

const formatUsd = (amount: string | number | null | undefined) =>
  `$${formatAmount(Number(amount ?? 0))}`

export const EarnAssetDetails = () => {
  const navigate = useNavigate()
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const setEarnDetailsBreadcrumb = useSetAtom(earnDetailsBreadcrumbAtom)
  const isDirectEarning = useIsDirectEarning()

  const { data, isLoading } = useEarnAssetDetailsQuery(portfolioId)

  useEffect(() => {
    setEarnDetailsBreadcrumb(data?.asset ?? '')
    return () => setEarnDetailsBreadcrumb('')
  }, [data?.asset, setEarnDetailsBreadcrumb])

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (!data) {
    navigate(PathNames.NOT_FOUND)
    return null
  }

  const earnRate = formatAPY(data.myEarnRate ?? 0)

  return (
    <Box flexCol gap={24}>
      <EarnAssetDetailsHeader
        asset={data.asset}
        earnRate={earnRate}
        onWithdrawClick={() => []}
        onDepositClick={() => []}
      />

      {isDirectEarning ? (
        <EarnAssetSummaryDirect
          asset={data.asset}
          earnRate={earnRate}
          allocatedFunds={formatUsd(data.fundsOverview.allocatedFunds)}
          myLifetimeEarnings={formatUsd(data.earningsSummary.myLifetimeEarnings)}
        />
      ) : (
        <>
          <EarnAssetFundsSummary
            asset={data.asset}
            earnRate={earnRate}
            allocatedFunds={formatUsd(data.fundsOverview.allocatedFunds)}
            totalCustomerEligibleFunds={formatUsd(data.fundsOverview.totalCustomerEligibleFunds)}
          />

          <EarnAssetEarningsSummary
            myLifetimeEarnings={formatUsd(data.earningsSummary.myLifetimeEarnings)}
            customerEarningsReceived={formatUsd(data.earningsSummary.customerEarningsReceived)}
            distributedEarningsToCustomers={formatUsd(
              data.earningsSummary.distributedEarningsToCustomers
            )}
          />
        </>
      )}

      <EarnAssetHistory portfolioId={portfolioId} walletId={data.walletId} />
    </Box>
  )
}
